import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Grid,
  Box,
  Card,
  IconButton,
  Input,
  InputAdornment,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import { get } from "aws-amplify/api";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { post } from "aws-amplify/api";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { APIStatusDialog } from "popup/APIStatusDialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import SplitterLayout from "react-splitter-layout";
import CaseDetailsSplit from "./components/CaseDetailsSplit";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Button from "@mui/material/Button";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";
const COLUMN_VISIBILITY_KEY = "columnVisibility_stores";
const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};
function Cases() {
  const [state, dispatch] = useReducer(reducer, { audit: [], fetched: false });
  const theme = useTheme();
  const mobileDevice = !useMediaQuery(theme.breakpoints.up("md"));
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [languages, setLanguages] = useState([]);
  const [profile, setProfile] = useState([]);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(3, "weeks").startOf("day")
  );
  const [endDate, setEndDate] = useState(dayjs().endOf("day"));
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const toolbarRef = useRef(null);
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("/settings")) {
      setTabValue(0);
    } else if (currentPath.includes("/uncoded")) {
      setTabValue(1);
    }
  }, []);
  useEffect(() => {
    const savedColumnVisibility = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (savedColumnVisibility) {
      setColumnVisibilityModel(JSON.parse(savedColumnVisibility));
    } else {
      setColumnVisibilityModel({
        id: false,
        source_technology: false,
        datetime_close: false,
        alarm_text: false,
        asset: false,
        serial_number: false,
        billing_city: true,
        billing_street: false,
        retailer: false,
        phone: false,
        email: true,
        billing_country: true,
        account_number: false,
        fax: false,
        secondary_note: false,
        primary_note: false,
        website: false,
        tls_contract_type: false,
        tls_contract_end_date: false,
        tls_contract_start_date: false,
        tls_activation_date: false,
        billing_postal_code: false,
        tlm_active: false,
        billing_country_code: false,
        store_management: true,
        store_name: false,
      });
    }
  }, []);

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const [searchText, setSearchText] = useState("");
  const [rowModesModel, setRowModesModel] = useState({});
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const filteredRows = state.audit.filter((value) => {
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      return Object.values(value).some((val) =>
        String(val).toLowerCase().includes(searchTextLower)
      );
    }
    return true;
  });

  const filteredRowsCount = filteredRows.length;
  const tlmGetSalesforceCase = async () => {
    try {
      const formattedStartDate = startDate.format("YYYY-MM-DD HH:mm:ss");
      const formattedEndDate = endDate.format("YYYY-MM-DD HH:mm:ss");
      setLoading(true);
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/salesforce/case/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            start_date: formattedStartDate,
            end_date: formattedEndDate,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      dispatch({ type: "DATA_RECEIVED", value: response });
      setLoading(false);
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    }
  };
  useEffect(() => {
    tlmGetSalesforceCase();
  }, []);

  useEffect(() => {
    const tlmGetLanguagesAPI = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/languages",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {},
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        setLanguages(response);
      } catch (error) {
        console.error("Errore durante la chiamata API:", error);
      }
    };
    tlmGetLanguagesAPI();
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "case_number",
        headerName: I18n.get("Case Number"),
        width: 200,
        renderCell: (params) => (
          <Typography
            component="a"
            href={
              process.env.REACT_APP_ENV_ENVIRONMENT != "prod"
                ? `https://eptarefrigeration--bugfix.sandbox.lightning.force.com/${params.row.case_id}`
                : `https://eptarefrigeration.lightning.force.com/${params.row.case_id}`
            }
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "creation_date",
        headerName: I18n.get("Creation Date"),
        width: 300,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "creation_agent",
        headerName: I18n.get("Creation Agent"),
        width: 250,
      },
      {
        field: "name",
        headerName: I18n.get("Account Name"),
        width: 400,
        renderCell: (params) => (
          <Typography
            component="a"
            href={`https://eptarefrigeration.lightning.force.com/${params.row.account_id}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "phone",
        headerName: I18n.get("Phone"),
        width: 200,
        renderCell: (params) => (
          <Typography
            component="a"
            href={`tel:${params.value}`}
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      { field: "fax", headerName: I18n.get("Fax"), width: 200 },
      {
        field: "email",
        headerName: I18n.get("Email"),
        width: 400,
        renderCell: (params) => (
          <Typography
            component="a"
            href={`mailto:${params.value}`}
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      { field: "active", headerName: I18n.get("Active"), width: 200 },
      {
        field: "billing_country",
        headerName: I18n.get("Country"),
        width: 200,
      },
      {
        field: "billing_city",
        headerName: I18n.get("City"),
        width: 200,
      },
      {
        field: "billing_country_code",
        headerName: I18n.get("Country Code"),
        width: 200,
      },
      {
        field: "billing_postal_code",
        headerName: I18n.get("Postal Code"),
        width: 200,
      },
      {
        field: "retailer",
        headerName: I18n.get("Retailer Code"),
        width: 200,
      },
      {
        field: "tlm_active",
        headerName: I18n.get("TLM Active"),
        width: 200,
      },
      {
        field: "retailer_description",
        headerName: I18n.get("Retailer"),
        width: 400,
      },
      {
        field: "store_management",
        headerName: I18n.get("Store Management"),
        width: 400,
      },
      {
        field: "tls_activation_date",
        headerName: I18n.get("Activation Date"),
        width: 200,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "tls_contract_start_date",
        headerName: I18n.get("Contract Start Date"),
        width: 200,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "tls_contract_end_date",
        headerName: I18n.get("Contract End Date"),
        width: 200,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "tls_contract_type",
        headerName: I18n.get("Contract Type"),
        width: 200,
      },
      {
        field: "website",
        headerName: I18n.get("Website"),
        width: 200,
      },
      {
        field: "store_name",
        headerName: I18n.get("Store Name"),
        width: 200,
        renderCell: (params) => (
          <Typography
            component="a"
            href={`https://eptarefrigeration.lightning.force.com/${params.row.account_id}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "case_id",
        headerName: I18n.get("Case ID"),
        width: 200,
      },
      {
        field: "account_id",
        headerName: I18n.get("Account ID"),
        width: 200,
      },
    ],
    []
  );

  const handlePopUpButtonClick = (selectedRow) => {
    setSelectedRowData([selectedRow]); // Ensure the selected row data is updated
    setShowPopup(true); // Show the popup when a row is clicked
  };

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(newModel));
  };

  const [loading, setLoading] = useState(false); //State for loading spinner

  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Typography variant="h5" mt={0.5} mb={1}>
            {I18n.get("Cases")} ({filters ? filteredRowsCount : totalCount})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          spacing={2}
          sx={{ pb: 2 }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={I18n.get("Start Date")}
              value={startDate ? dayjs(startDate) : null} // Converte startDate in oggetto dayjs
              onChange={(newValue) => setStartDate(newValue)}
              format="YYYY/MM/DD"
              maxDate={dayjs()}
              renderInput={(params) => (
                <Input {...params} fullWidth={tableParentIsMD} />
              )}
            />
            <DatePicker
              label={I18n.get("End Date")}
              value={endDate ? dayjs(endDate) : null} // Converte endDate in oggetto dayjs
              onChange={(newValue) => setEndDate(newValue)}
              format="YYYY/MM/DD"
              maxDate={dayjs()}
              renderInput={(params) => (
                <Input {...params} fullWidth={tableParentIsMD} />
              )}
            />
          </LocalizationProvider>

          <Button
            color="primary"
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => tlmGetSalesforceCase()} // Calls the API with updated date values
          >
            {I18n.get("Search")}
          </Button>
          {loading && <CircularProgress size="1.5rem" />}
          <Box
            sx={{
              ml: 2,
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Input
              type="text"
              placeholder={I18n.get("Search")}
              onChange={searchChanged}
              fullWidth={tableParentIsMD}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
        </Stack>
      </div>
    );
  };
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    }
  }
  const browser = getBrowserName();
  useEffect(() => {
    if (browser === "Firefox") {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "auto";
      };
    }
  }, []);
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        90;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  const darkMode = localStorage.getItem("darkMode") === "true";
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
          profile={profile} // Pass profile
          setProfile={setProfile}
        />
        <SplitterLayout
          vertical={mobileDevice}
          percentage={true}
          primaryIndex={0}
          primaryMinSize={mobileDevice ? 30 : 50}
          secondaryMinSize={35}
          secondaryInitialSize={mobileDevice ? 30 : 50}
          customClassName="splitter-layout-custom"
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
                {" "}
                {/** lasciare così */}
                <Card
                  sx={{
                    p: 3,
                    maxHeight: `${tableHeight}px`,
                  }}
                >
                  {renderToolbar()}
                  <DataGridPro
                    localeText={
                      language === "it"
                        ? itIT.components.MuiDataGrid.defaultProps.localeText
                        : language === "ge"
                        ? deDE.components.MuiDataGrid.defaultProps.localeText
                        : ""
                    }
                    loading={!state.fetched || resizeThrottle > 60}
                    initialState={{
                      density: "compact",

                      pinnedColumns: { right: ["action"] },
                    }}
                    sx={{
                      "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
                        minHeight: "2rem",
                      },
                      "&  .MuiDataGrid-cell ": {
                        cursor: "default",
                      },
                      borderColor: darkMode ? "#6B635A" : "",
                      "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "--DataGrid-rowBorderColor": darkMode ? "#625a52" : "",
                      minHeight: "20vh",
                      flexFlow: 1,
                      height: "100%",
                    }}
                    resizeThrottleMs={resizeThrottle}
                    rows={filteredRows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={(newModel) =>
                      setRowModesModel(newModel)
                    }
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    //pagination
                    hideFooter
                    autoPageSize
                    onRowClick={(params) => handlePopUpButtonClick(params.row)}
                    slotProps={{
                      loadingOverlay: {
                        variant: "skeleton",
                        noRowsVariant: "skeleton",
                      },
                    }}
                    slots={{
                      toolbar: () => (
                        <GridToolbarContainer>
                          <GridToolbarColumnsButton
                            title={I18n.get("Columns")}
                          />
                          <GridToolbarExport
                            printOptions={{ disableToolbarButton: true }}
                            csvOptions={{
                              fileName: I18n.get("Store List"),
                              utf8WithBom: true,
                            }}
                            title={I18n.get("Export")}
                          />
                          <GridToolbarFilterButton
                            title={I18n.get("Filters")}
                          />
                        </GridToolbarContainer>
                      ),
                    }}
                  />
                </Card>
              </Box>
            </Grid>
          </Grid>
          {showPopup && (
            <CaseDetailsSplit
              isOpen={showPopup}
              onClose={() => {
                setShowPopup(false);
              }}
              onCloseNote={() => {
                tlmGetSalesforceCase();
              }}
              profile={profile}
              rowData={selectedRowData}
            />
          )}
        </SplitterLayout>
      </DashboardLayout>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </>
  );
}

export default Cases;
